import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import AdminNav from '../components/AdminNav'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const RED    = '#DC2626'

const SLOT_TYPES = [
  { key: 'top_banner',    label: 'Top Banner',    desc: 'Full width · appears above orders', size: '320×80px',  icon: '📢' },
  { key: 'mid_mrec',      label: 'Mid MREC',      desc: 'Square · appears between orders and directory', size: '300×250px', icon: '🖼️' },
  { key: 'bottom_banner', label: 'Bottom Banner', desc: 'Full width · appears above footer', size: '320×80px',  icon: '📌' },
]

function SField({ label, value, onChange, placeholder, optional }) {
  const [focused, setFocused] = useState(false)
  return (
    <div>
      <label style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '4px' }}>
        {label}{optional && <span style={{ color: '#B0B3B8', fontWeight: 400, marginLeft: '4px' }}>optional</span>}
      </label>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: '100%', border: '1px solid ' + (focused ? BLUE : BORDER), borderRadius: '9px', padding: '8px 12px', fontSize: '12.5px', outline: 'none', background: WHITE, color: TEXT, fontFamily: 'Space Grotesk, sans-serif', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
    </div>
  )
}

function AdminAdSlots() {
  const [slots, setSlots]       = useState({})
  const [loading, setLoading]   = useState(true)
  const [saving, setSaving]     = useState(null)
  const [uploading, setUploading] = useState(null)
  const [saved, setSaved]       = useState('')
  const [editForm, setEditForm] = useState({})

  useEffect(() => { fetchSlots() }, [])

  const fetchSlots = async () => {
    setLoading(true)
    const { data } = await supabase.from('ad_slots').select('*')
    const slotMap = {}
    data?.forEach(s => { slotMap[s.slot_type] = s })
    setSlots(slotMap)
    const forms = {}
    SLOT_TYPES.forEach(t => {
      forms[t.key] = {
        link_url:        slotMap[t.key]?.link_url        || '',
        advertiser_name: slotMap[t.key]?.advertiser_name || '',
        notes:           slotMap[t.key]?.notes           || '',
        is_active:       slotMap[t.key]?.is_active       || false,
      }
    })
    setEditForm(forms)
    setLoading(false)
  }

  const flash = (msg) => { setSaved(msg); setTimeout(() => setSaved(''), 3000) }

  const handleUpload = async (slotType, file) => {
    if (!file) return
    setUploading(slotType)
    const ext      = file.name.split('.').pop()
    const filename = 'ad-' + slotType + '-' + Date.now() + '.' + ext
    const { error: upErr } = await supabase.storage.from('ad-images').upload(filename, file, { upsert: true })
    if (upErr) { alert('Upload failed: ' + upErr.message); setUploading(null); return }
    const { data: urlData } = supabase.storage.from('ad-images').getPublicUrl(filename)
    const imageUrl = urlData.publicUrl
    const existing = slots[slotType]
    if (existing) {
      await supabase.from('ad_slots').update({ image_url: imageUrl }).eq('id', existing.id)
    } else {
      await supabase.from('ad_slots').insert({ slot_type: slotType, image_url: imageUrl, link_url: '', is_active: false })
    }
    await fetchSlots()
    setUploading(null)
    flash('Image uploaded!')
  }

  const handleSave = async (slotType) => {
    setSaving(slotType)
    const form     = editForm[slotType]
    const existing = slots[slotType]
    const payload  = { link_url: form.link_url, advertiser_name: form.advertiser_name, notes: form.notes, is_active: form.is_active }
    if (existing) {
      await supabase.from('ad_slots').update(payload).eq('id', existing.id)
    } else {
      await supabase.from('ad_slots').insert({ slot_type: slotType, ...payload })
    }
    await fetchSlots()
    setSaving(null)
    flash('Saved!')
  }

  const handleRemoveImage = async (slotType) => {
    const existing = slots[slotType]
    if (!existing) return
    await supabase.from('ad_slots').update({ image_url: null, is_active: false }).eq('id', existing.id)
    setEditForm(f => ({ ...f, [slotType]: { ...f[slotType], is_active: false } }))
    await fetchSlots()
    flash('Image removed')
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <AdminNav />
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <div style={{ padding: '24px 28px' }}>
          <style>{`@media(max-width:700px){ .adslot-grid{grid-template-columns:1fr!important} }`}</style>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: TEXT, letterSpacing: '-0.3px' }}>Ad Slots</div>
              <div style={{ fontSize: '12.5px', color: MUTED, marginTop: '2px' }}>Manage marketing spaces on the customer tracking portal</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {saved && <span style={{ fontSize: '12px', fontWeight: 600, color: GREEN }}>{saved}</span>}
              <button onClick={fetchSlots} style={{ fontSize: '12px', fontWeight: 600, color: MUTED, background: WHITE, border: '1px solid ' + BORDER, padding: '7px 14px', borderRadius: '9px', cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>↻ Refresh</button>
            </div>
          </div>

          {/* Info banner */}
          <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '12px', padding: '14px 18px', marginBottom: '18px', fontSize: '12.5px', color: BLUE, lineHeight: 1.6 }}>
            💡 Ad slots appear on the customer tracking page at <code style={{ fontFamily: 'monospace', background: '#DBEAFE', padding: '1px 5px', borderRadius: '4px' }}>/track</code>.
            Only slots with an uploaded image AND the active toggle ON will show. Empty slots are completely hidden — no blank spaces.
          </div>

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px', color: MUTED, fontSize: '13px' }}>Loading ad slots...</div>
          )}

          {!loading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {SLOT_TYPES.map(slotType => {
                const slot    = slots[slotType.key]
                const form    = editForm[slotType.key] || {}
                const isLive  = slot?.is_active && slot?.image_url

                return (
                  <div key={slotType.key} style={{ background: WHITE, borderRadius: '12px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>

                    {/* Card header */}
                    <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + BORDER, background: BG, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '22px' }}>{slotType.icon}</span>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 800, color: TEXT }}>{slotType.label}</div>
                          <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '1px' }}>{slotType.desc} · {slotType.size}</div>
                        </div>
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 12px', borderRadius: '20px', background: isLive ? '#F0FDF4' : BG, color: isLive ? GREEN : MUTED, border: '1px solid ' + (isLive ? '#BBF7D0' : BORDER) }}>
                        {isLive ? '🟢 Live' : '⚪ Inactive'}
                      </span>
                    </div>

                    <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '20px', alignItems: 'start' }} className="adslot-grid">

                      {/* Left: image upload */}
                      <div>
                        <div style={{ fontSize: '11.5px', fontWeight: 600, color: MUTED, marginBottom: '8px' }}>Ad image <span style={{ fontWeight: 400, color: '#B0B3B8' }}>({slotType.size} recommended)</span></div>
                        {slot?.image_url ? (
                          <div style={{ position: 'relative', marginBottom: '10px' }}>
                            <img src={slot.image_url} alt="Ad preview"
                              style={{ width: '100%', borderRadius: '10px', border: '1px solid ' + BORDER, objectFit: 'cover', maxHeight: '120px', display: 'block' }} />
                            <button onClick={() => handleRemoveImage(slotType.key)}
                              style={{ position: 'absolute', top: '6px', right: '6px', background: RED, color: WHITE, border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                              Remove
                            </button>
                          </div>
                        ) : (
                          <div style={{ background: BG, border: '2px dashed ' + BORDER, borderRadius: '10px', padding: '28px', textAlign: 'center', marginBottom: '10px' }}>
                            <div style={{ fontSize: '24px', marginBottom: '6px' }}>🖼️</div>
                            <div style={{ fontSize: '12px', color: MUTED }}>No image uploaded</div>
                          </div>
                        )}
                        <label style={{ display: 'block', cursor: uploading === slotType.key ? 'not-allowed' : 'pointer' }}>
                          <input type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={e => e.target.files?.[0] && handleUpload(slotType.key, e.target.files[0])} />
                          <div style={{ textAlign: 'center', padding: '9px', borderRadius: '9px', border: '1px solid ' + (uploading === slotType.key ? BORDER : BLUE), background: uploading === slotType.key ? BG : '#EFF6FF', color: uploading === slotType.key ? MUTED : BLUE, fontSize: '12.5px', fontWeight: 700, transition: 'all 0.15s' }}>
                            {uploading === slotType.key ? '⏳ Uploading...' : '📁 Upload image'}
                          </div>
                        </label>
                      </div>

                      {/* Right: form fields */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <SField label="Click destination URL" value={form.link_url || ''} onChange={v => setEditForm(f => ({ ...f, [slotType.key]: { ...f[slotType.key], link_url: v } }))} placeholder="https://facebook.com/sellershop" />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          <SField label="Advertiser name" value={form.advertiser_name || ''} onChange={v => setEditForm(f => ({ ...f, [slotType.key]: { ...f[slotType.key], advertiser_name: v } }))} placeholder="Riya's Boutique" optional />
                          <SField label="Internal notes" value={form.notes || ''} onChange={v => setEditForm(f => ({ ...f, [slotType.key]: { ...f[slotType.key], notes: v } }))} placeholder="৳5000/week" optional />
                        </div>

                        {/* Toggle + Save */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '10px', borderTop: '1px solid ' + BORDER }}>
                          <div
                            onClick={() => setEditForm(f => ({ ...f, [slotType.key]: { ...f[slotType.key], is_active: !f[slotType.key]?.is_active } }))}
                            style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                            <div style={{ width: '40px', height: '22px', borderRadius: '11px', background: form.is_active ? GREEN : BORDER, position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                              <div style={{ position: 'absolute', top: '3px', left: form.is_active ? '21px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: WHITE, boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: form.is_active ? GREEN : MUTED }}>
                              {form.is_active ? 'Active — showing to customers' : 'Inactive — hidden'}
                            </span>
                          </div>
                          <button onClick={() => handleSave(slotType.key)} disabled={saving === slotType.key}
                            style={{ padding: '8px 20px', borderRadius: '9px', background: saving === slotType.key ? BORDER : BLUE, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: saving === slotType.key ? 'not-allowed' : 'pointer', fontFamily: 'Space Grotesk, sans-serif' }}>
                            {saving === slotType.key ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

export default AdminAdSlots